import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import DataTable, { type DataTableColumn } from '@/components/ui/data-table'
import { PageHeader } from '@/components/ui/page-header'
import { SearchInput } from '@/components/ui/search-input'
import { StatusPill } from '@/components/ui/status'
import { OrderMoneyCell } from '@/components/orders/order-money'
import { money, formatDateTime } from '@/lib/format'
import { get } from '@/lib/api'
import { getAuthState } from '@/lib/auth'
import { ORDER_FILTER_STATUSES, STATUS_LABEL } from '@/lib/orderStatus'
import { DateRangeFilter, applyDateFilter, useDateFilterParams } from '@/components/orders/DateRangeFilter'
import { rangeSummary } from '@shoptrack/common/date-range'
import { UnitFilter, applyUnitChoice, useUnitChoice } from '@/components/network/UnitFilter'

export default function AgencyOrders() {
  const navigate = useNavigate()
  const selfShopId = getAuthState().activeShopId
  const [searchTerm, setSearchTerm] = useState('')
  // ?status=<trạng thái> (sơ đồ hành trình ở Tổng quan) chọn sẵn bộ lọc
  const [params] = useSearchParams()
  const [selectedStatus, setSelectedStatus] = useState<string>(() => {
    const st = params.get('status')
    return st && (ORDER_FILTER_STATUSES as readonly string[]).includes(st) ? st : 'all'
  })
  // Lọc theo ngày tạo đơn — giữ trên URL
  const [dateFilter, setDateFilter] = useDateFilterParams()
  // Lọc theo người bán (?unit=): tất cả · của tôi · cấp dưới · từng đơn vị
  const [unit, setUnit] = useUnitChoice(selfShopId)

  // Lấy đơn hàng THẬT từ API
  const { data: ordersData, isLoading } = useQuery({
    queryKey: ['orders', selectedStatus, searchTerm, dateFilter.from, dateFilter.to, unit],
    queryFn: () => {
      const params = applyDateFilter(new URLSearchParams(), dateFilter)
      params.set('limit', '100')
      // Mặc định scope=all: đơn tự bán + đơn của mọi đơn vị trong nhánh
      applyUnitChoice(params, unit, selfShopId)
      if (selectedStatus !== 'all') params.set('status', selectedStatus)
      if (searchTerm) params.set('q', searchTerm)
      return get<{ orders: any[] }>(`/orders?${params.toString()}`)
    },
  })

  const orders = ordersData?.orders ?? []

  const columns: DataTableColumn<any>[] = [
    {
      key: 'order',
      header: 'Mã đơn',
      priority: 'primary',
      cell: (o) => (
        <div>
          <p className="font-mono font-bold text-sm text-ink">{o.clientOrderId || o.id}</p>
          {o.shopName && <p className="text-xs font-medium text-accent">{o.isOwn || o.shopId === selfShopId ? 'Của tôi' : o.shopName}</p>}
          <span className="text-xs text-muted font-mono">{formatDateTime(o.createdAt)}</span>
        </div>
      ),
    },
    {
      key: 'customer',
      header: 'Khách hàng',
      priority: 'secondary',
      cell: (o) => (
        <div>
          <p className="font-medium text-inkSoft">{o.customer?.name || 'Khách lẻ'}</p>
          <p className="text-muted font-mono">{o.customer?.phone || '—'}</p>
          <p className="text-xs text-inkSoft truncate max-w-xs">{o.address}</p>
        </div>
      ),
    },
    {
      key: 'channel',
      header: 'Kênh',
      cell: (o) => o.channel,
    },
    {
      key: 'tracking',
      header: 'Vận đơn',
      mobileLabel: 'Vận đơn',
      cell: (o) => (
        <span className="font-mono font-semibold text-ink">{o.shipmentCode || 'Chưa có vận đơn'}</span>
      ),
    },
    {
      key: 'cod',
      header: 'COD',
      align: 'right',
      cell: (o) => <span className="font-mono font-bold text-ink">{money(o.cod)}</span>,
    },
    {
      key: 'money',
      header: 'Tiền của bạn',
      mobileLabel: 'Tiền của bạn',
      align: 'right',
      // Đơn tự bán: thực nhận. Đơn trong nhánh: chênh lệch / hoa hồng của Đại lý trên đơn đó.
      cell: (o) => <OrderMoneyCell money={o.money} seller={!o.shopId || o.shopId === selfShopId} showRole />,
    },
    {
      key: 'status',
      header: 'Trạng thái',
      align: 'center',
      cell: (o) => <StatusPill status={o.status} />,
    },
  ]

  return (
    <div className="mx-auto max-w-7xl space-y-5">
      <PageHeader title="Đơn hàng" />

      <div className="flex flex-wrap items-center gap-2">
        <div className="w-full sm:w-72">
          <SearchInput
            aria-label="Tìm mã đơn, mã vận đơn, kênh bán"
            placeholder="Mã đơn, mã vận đơn, kênh bán…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <DateRangeFilter value={dateFilter} onChange={setDateFilter} />
        <UnitFilter value={unit} onChange={setUnit} />
        <select
          value={selectedStatus}
          onChange={(e) => setSelectedStatus(e.target.value)}
          aria-label="Lọc theo trạng thái"
          className="h-11 desktop:h-10 rounded-lg border border-rule bg-raise px-3 text-sm "
        >
          <option value="all">Tất cả trạng thái</option>
          {ORDER_FILTER_STATUSES.map((st) => (
            <option key={st} value={st}>
              {STATUS_LABEL[st]?.text ?? st}
            </option>
          ))}
        </select>
        <span className="text-xs text-muted sm:ml-auto">
          {orders.length >= 100 ? '100 đơn mới nhất' : `${orders.length} đơn`}
          {dateFilter.key !== 'all' && (dateFilter.from || dateFilter.to) && ` · tạo ${rangeSummary(dateFilter.from, dateFilter.to)}`}
        </span>
      </div>

      <div className="overflow-hidden rounded-xl bg-raise shadow-card">
        <DataTable
          columns={columns}
          data={orders}
          rowKey={(o, idx) => `${o.shopId || ''}_${o.id}_${idx}`}
          loading={isLoading}
          // Đơn của shop / CTV trong nhánh: mở chế độ chỉ xem (?shopId=…); đơn tự bán: như bình thường.
          onRowClick={(o) =>
            navigate(o.shopId && o.shopId !== selfShopId ? `/orders/${encodeURIComponent(o.id)}?shopId=${encodeURIComponent(o.shopId)}` : `/orders/${encodeURIComponent(o.id)}`)
          }
          emptyLabel={dateFilter.key !== 'all' ? 'Không có đơn' : 'Chưa có đơn'}
        />
      </div>
    </div>
  )
}
